/**
 * Pure helpers for the generation-context API + structured seed-prompt artifact.
 * Used by the "How generated" panel (Sources / Seed / Trace).
 */

import type {
  GenerationContextManifest,
  GenerationContextResponse,
  StructuredSeedPrompt,
  StructuredSeedRef,
} from '@/lib/api/quality-types.js';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Strip a leading `runs/<runId>/` prefix so callers can pass the key to
 * `artifactUrl()` / `fetchArtifactText`, which expect run-relative keys.
 */
function toRunRelativeArtifactKey(runId: string, key: string): string {
  const prefix = `runs/${runId}/`;
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}

/**
 * Whether the Sources section has anything worth rendering (404/null manifest → hide).
 */
export function hasManifestContent(
  manifest: GenerationContextManifest | null | undefined,
): boolean {
  if (!manifest) return false;
  const liveAppHasContent = Boolean(
    manifest.live_app?.start_url?.trim() ||
    manifest.live_app?.credential_login_as?.trim(),
  );
  return Boolean(
    manifest.instructions?.trim() ||
    manifest.documents?.length ||
    manifest.context_sources?.length ||
    manifest.existing_tests?.length ||
    liveAppHasContent ||
    manifest.mode ||
    manifest.platform ||
    manifest.claude_model ||
    manifest.max_cases != null,
  );
}

/**
 * Prefer the highest `version` number that carries a structured seed key; null when none.
 * Order in the versions array does not matter.
 */
export function resolveStructuredSeedKey(
  context: GenerationContextResponse | null | undefined,
): string | null {
  if (!context?.versions?.length) return null;
  let bestKey: string | null = null;
  let bestVersion = Number.NEGATIVE_INFINITY;
  for (const versionEntry of context.versions) {
    const structuredKey = versionEntry?.seed_prompt?.structured_key?.trim();
    if (!structuredKey) continue;
    const versionNumber =
      typeof versionEntry.version === 'number'
        ? versionEntry.version
        : Number.NEGATIVE_INFINITY;
    if (versionNumber >= bestVersion) {
      bestVersion = versionNumber;
      bestKey = structuredKey;
    }
  }
  return bestKey ? toRunRelativeArtifactKey(context.run_id, bestKey) : null;
}

/**
 * Scrubbed markdown key for the optional rendered-prompt viewer: highest-version
 * entry's `rendered_key` first, else the structured seed's `rendered_redacted_key`.
 */
export function resolveRenderedSeedKey(
  context: GenerationContextResponse | null | undefined,
  seed: StructuredSeedPrompt | null | undefined,
): string | null {
  if (context?.versions?.length) {
    let bestRenderedKey: string | null = null;
    let bestVersion = Number.NEGATIVE_INFINITY;
    let foundStructured = false;
    for (const versionEntry of context.versions) {
      const seedPrompt = versionEntry?.seed_prompt;
      if (!seedPrompt?.structured_key?.trim()) continue;
      const versionNumber =
        typeof versionEntry.version === 'number'
          ? versionEntry.version
          : Number.NEGATIVE_INFINITY;
      if (versionNumber < bestVersion) continue;
      bestVersion = versionNumber;
      foundStructured = true;
      bestRenderedKey = seedPrompt.rendered_key?.trim() || null;
    }
    if (foundStructured && bestRenderedKey) {
      return toRunRelativeArtifactKey(context.run_id, bestRenderedKey);
    }
  }
  const fromSeed = seed?.rendered_redacted_key?.trim();
  if (!fromSeed) return null;
  return context?.run_id
    ? toRunRelativeArtifactKey(context.run_id, fromSeed)
    : fromSeed;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asRefs(value: unknown): StructuredSeedRef[] {
  if (!Array.isArray(value)) return [];
  const refs: StructuredSeedRef[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    refs.push({
      artifact_key: asString(entry.artifact_key),
      prompt_path_basename: asString(entry.prompt_path_basename),
    });
  }
  return refs;
}

/**
 * Parse structured seed-prompt JSON. Returns null on garbage. Forces `login.password`
 * to null so a buggy artifact can never surface a secret in the UI.
 */
export function parseStructuredSeedPrompt(
  raw: string,
): StructuredSeedPrompt | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  if (
    typeof parsed.schema_version !== 'number' ||
    typeof parsed.version !== 'number'
  ) {
    return null;
  }
  const loginRaw = isRecord(parsed.login) ? parsed.login : {};
  const liveRaw = isRecord(parsed.live_app) ? parsed.live_app : null;
  const revisionRaw = isRecord(parsed.revision) ? parsed.revision : null;
  return {
    schema_version: parsed.schema_version,
    version: parsed.version,
    mode: asString(parsed.mode),
    template: asString(parsed.template),
    instructions: asString(parsed.instructions),
    max_cases_clause: asString(parsed.max_cases_clause),
    login: {
      required: loginRaw.required === true,
      login_as:
        typeof loginRaw.login_as === 'string' || loginRaw.login_as === null
          ? (loginRaw.login_as as string | null)
          : null,
      password_present: loginRaw.password_present === true,
      password: null,
    },
    document_refs: asRefs(parsed.document_refs),
    existing_tests_refs: asRefs(parsed.existing_tests_refs),
    live_app: liveRaw
      ? { start_url_redacted: asString(liveRaw.start_url_redacted) }
      : null,
    revision: revisionRaw
      ? {
          feedback: revisionRaw.feedback ?? null,
          revise_indices: Array.isArray(revisionRaw.revise_indices)
            ? revisionRaw.revise_indices.filter(
                (index): index is number => typeof index === 'number',
              )
            : [],
        }
      : null,
    rendered_redacted_key:
      typeof parsed.rendered_redacted_key === 'string'
        ? parsed.rendered_redacted_key
        : parsed.rendered_redacted_key === null
          ? null
          : undefined,
  };
}
